import { ReactNode } from "react";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Separator } from "@design-system/Separator/Separator";
import { BulkSelectionControl } from "@design-system/BulkSelectionControl/BulkSelectionControl";
import { HStack } from "@design-system/HStack/HStack";
import { EditorIssueBar } from "../EditorIssueBar";
import { EditorReturnAction } from "../editorBodyTypes";
import { NestedEditorBulkActionsMenu } from "./NestedEditorBulkActionsMenu";
import styles from "./ChartOfAccountsTemplateNestedEditor.module.scss";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

export interface NestedEditorCommandIslandProps {
    selectedCount: number;
    actionableCount: number;
    canBulkEnable: boolean;
    canBulkDisable: boolean;
    canBulkDelete: boolean;
    bulkArchiveBlockReason: string | undefined;
    bulkDeleteBlockReason: string | undefined;
    issueCount: number;
    currentIssueIndex: number;
    currentIssueMessage: string | undefined;
    isSaving: boolean;
    isSaveDisabled: boolean;
    saveBlockedReason: string | undefined;
    // Host-supplied so the island does not say "template".
    saveHotkeyDescription: string;
    showSaveActions: boolean;
    // Save and the add modal share mod+enter; disabling Save lets ⌘↵ hit Create.
    isAddModalOpen: boolean;
    onClearSelection: () => void;
    onBulkEnable: () => void;
    onBulkDisable: () => void;
    onBulkDelete: () => void;
    onNavigateIssue: (index: number) => void;
    onAddGroup: () => void;
    onAddAccount: () => void;
    onSave: () => void;
    onSaveAndClose: () => void;
    isEditingDisabled: boolean;
    islandAddon?: ReactNode;
    returnAction?: EditorReturnAction;
}

type Props = NestedEditorCommandIslandProps;

export const NestedEditorCommandIsland = ({
    selectedCount,
    actionableCount,
    canBulkEnable,
    canBulkDisable,
    canBulkDelete,
    bulkArchiveBlockReason,
    bulkDeleteBlockReason,
    issueCount,
    currentIssueIndex,
    currentIssueMessage,
    isSaving,
    isSaveDisabled,
    saveBlockedReason,
    saveHotkeyDescription,
    showSaveActions,
    isAddModalOpen,
    onClearSelection,
    onBulkEnable,
    onBulkDisable,
    onBulkDelete,
    onNavigateIssue,
    onAddGroup,
    onAddAccount,
    onSave,
    onSaveAndClose,
    isEditingDisabled,
    islandAddon,
    returnAction,
}: Props) => {
    const hasSelection = selectedCount > 0;
    const hasActionableSelection = actionableCount > 0 || canBulkDelete;
    const hasIssues = issueCount > 0;

    return (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <HStack
                gap={8}
                align="center"
                className={styles.commandIslandLeading}
            >
                {islandAddon && (
                    <div className={styles.commandIslandAddon}>
                        {islandAddon}
                    </div>
                )}
                {hasSelection && (
                    <BulkSelectionControl
                        selectedCount={selectedCount}
                        onClearSelection={onClearSelection}
                        size="md"
                    />
                )}
                {hasIssues && (
                    <>
                        <Separator
                            orientation="vertical"
                            className={styles.commandIslandSeparator}
                        />
                        <EditorIssueBar
                            issueCount={issueCount}
                            currentIssueIndex={currentIssueIndex}
                            currentIssueMessage={currentIssueMessage}
                            onNavigateIssue={onNavigateIssue}
                        />
                    </>
                )}
            </HStack>
            <HStack gap={8} align="center" className="ml-auto">
                {hasActionableSelection && !isEditingDisabled && (
                    <NestedEditorBulkActionsMenu
                        canBulkEnable={canBulkEnable}
                        canBulkDisable={canBulkDisable}
                        canBulkDelete={canBulkDelete}
                        bulkArchiveBlockReason={bulkArchiveBlockReason}
                        bulkDeleteBlockReason={bulkDeleteBlockReason}
                        onBulkEnable={onBulkEnable}
                        onBulkDisable={onBulkDisable}
                        onBulkDelete={onBulkDelete}
                    />
                )}
                <WithHotkey
                    hotKey="alt+g"
                    hotKeyOptions={{
                        description: "Add group",
                        preventDefault: true,
                    }}
                    hotKeyDisplayPosition="inline"
                >
                    {(hotkeyHint) => (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={onAddGroup}
                            disabled={isAddModalOpen || isEditingDisabled}
                            rightIcon={hotkeyHint}
                        >
                            Add group
                        </Button>
                    )}
                </WithHotkey>
                <WithHotkey
                    hotKey="alt+a"
                    hotKeyOptions={{
                        description: "Add account",
                        preventDefault: true,
                    }}
                    hotKeyDisplayPosition="inline"
                >
                    {(hotkeyHint) => (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={onAddAccount}
                            disabled={isAddModalOpen || isEditingDisabled}
                            rightIcon={hotkeyHint}
                        >
                            Add account
                        </Button>
                    )}
                </WithHotkey>
                {showSaveActions && (
                    <>
                        <Separator
                            orientation="vertical"
                            className={styles.commandIslandSeparator}
                        />
                        <WithHotkey
                            hotKey="mod+enter"
                            hotKeyOptions={{
                                description: saveHotkeyDescription,
                                preventDefault: true,
                                enableOnFormTags: true,
                            }}
                            hotKeyDisplayPosition="inline"
                            tooltip={saveBlockedReason}
                        >
                            {(hotkeyHint) => (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={onSave}
                                    disabled={isSaveDisabled || isAddModalOpen}
                                    rightIcon={hotkeyHint}
                                >
                                    {isSaving ? "Saving..." : "Save"}
                                </Button>
                            )}
                        </WithHotkey>
                        <Tooltip content={saveBlockedReason}>
                            <Button
                                variant="primary"
                                size="md"
                                onClick={onSaveAndClose}
                                disabled={isSaveDisabled || isAddModalOpen}
                            >
                                Save and close
                            </Button>
                        </Tooltip>
                    </>
                )}
                {returnAction && (
                    <>
                        <Separator
                            orientation="vertical"
                            className={styles.commandIslandSeparator}
                        />
                        <Button
                            variant="primary"
                            size="md"
                            onClick={returnAction.onClick}
                        >
                            {returnAction.label}
                        </Button>
                    </>
                )}
            </HStack>
        </CommandIsland>
    );
};
